import { memo } from 'react';
import { Button, IconHighlight, Skeleton } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { FolderOpen, KeyRound, LayoutGrid, LogOut, Shield, Users } from 'lucide-react';

const ITEM_ICON = {
  studios: LayoutGrid,
  roles: Shield,
  groups: Users,
  directPermissions: KeyRound,
  resources: FolderOpen,
  sessions: LogOut,
};

/**
 * Step 1 of the Remove flow: what the member loses, with Cancel and Continue.
 *
 * @param {object} props
 * @param {string} props.name
 * @param {{ key: string, count: number, names?: string }[]} props.summary From `buildRemovalSummary`.
 * @param {boolean} props.isLoading
 * @param {boolean} props.hasError The summary could not be loaded (the flow can still go on).
 * @param {() => void} props.onCancel
 * @param {() => void} props.onContinue
 */
const RemoveMemberStep1 = ({ name, summary, isLoading, hasError, onCancel, onContinue }) => {
  const { t } = useTranslation('people-access');

  let body;
  if (isLoading) {
    body = (
      <div className="flex flex-col gap-2" data-testid="remove-summary-loading">
        <Skeleton height="28px" />
        <Skeleton height="28px" />
      </div>
    );
  } else if (hasError) {
    body = <p className="text-sm text-content-secondary">{t('removeFlow.loadError', { name })}</p>;
  } else if (!summary.length) {
    body = <p className="text-sm text-content-secondary">{t('removeFlow.nothing', { name })}</p>;
  } else {
    body = (
      <ul className="flex flex-col divide-y divide-edge">
        {summary.map(({ key, count, names }) => {
          const Icon = ITEM_ICON[key];
          return (
            <li key={key} className="flex items-center gap-3 py-2.5 text-sm text-content">
              <IconHighlight icon={Icon} size="small" bgColor="Error" iconColor="Error" />
              <span>{t(`removeFlow.item.${key}`, { count, names })}</span>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <>
      <div className="px-6 py-4">{body}</div>
      <div className="flex justify-end gap-2 px-6 py-4">
        <Button variant="secondary" size="sm" data-autofocus onClick={onCancel}>
          {t('removeFlow.cancel')}
        </Button>
        <Button size="sm" onClick={onContinue} disabled={isLoading}>
          {t('removeFlow.continue')}
        </Button>
      </div>
    </>
  );
};

export default memo(RemoveMemberStep1);
